import type { SimpleChanges, TemplateRef } from '@angular/core';
import {
  Component,
  inject,
  output,
  signal,
  input,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';

import { debounceTime, startWith } from 'rxjs/operators';

import { injectCountryList } from '@core/country-data';
import { I18nService } from '@core/i18n-legacy';
import { OfferStatus } from '@core/offers-types';
import { DropdownWidth } from '@core/user-interface-legacy';
import { groupByKey, includesWithTrim } from '@core/utils-legacy';

import { SelectionList, SelectionListGroup } from '../offer-select.types';
import { OfferSelectionListImports } from './offers-selection-list.imports';
import {
  FixedItemData,
  GroupedSelectionItems,
  ItemData,
  ListData,
  SortingGroup,
  SortOptions,
  SortOptionsType,
} from './offers-selection-list.types';

@Component({
  selector: 'rt-offers-selection-list',
  templateUrl: './offers-selection-list.component.html',
  styleUrls: ['./offers-selection-list.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: OfferSelectionListImports,
})
export class OffersSelectionListComponent {
  private readonly i18n = inject(I18nService);

  public readonly listData = input.required<ListData>();
  public readonly jobAssignments = input<number[]>([]);
  public readonly talentPoolAssignments = input<number[]>([]);
  public readonly jobTemplateAssignments = input<number[]>([]);
  public readonly isLoading = input<boolean>(false);
  public readonly offerTemplate = input<TemplateRef<any>>();
  public readonly isSortingEnabled = input<boolean>(true);

  public readonly selectionChanges = output<{
    jobIds: number[];
    talentPoolIds: number[];
    jobTemplateIds: number[];
  }>();

  public readonly fixedItemData: GroupedSelectionItems<FixedItemData> = {
    [SelectionListGroup.Job]: {
      title: this.i18n.translate('app.common.jobs'),
      emptyStateTitle: this.i18n.translate('app.offers.empty_states.jobs.title'),
      sortable: true,
    },
    [SelectionListGroup.TalentPool]: {
      title: this.i18n.translate('app.common.talent_pools'),
      emptyStateTitle: this.i18n.translate('app.offers.empty_states.talent_pools.title'),
      sortable: false,
    },
    [SelectionListGroup.JobTemplate]: {
      title: this.i18n.translate('app.common.job_templates'),
      emptyStateTitle: this.i18n.translate('app.job_templates.no_templates'),
      sortable: false,
    },
  };
  public readonly DropdownWidth = DropdownWidth;
  public readonly SortOptions = SortOptions;

  public searchControl = new FormControl<string>('', { nonNullable: true });
  public searchValue = toSignal(
    this.searchControl.valueChanges.pipe(debounceTime(50), startWith('')),
    { initialValue: '' },
  );
  public availableTabs = computed<SelectionListGroup[]>(() =>
    this.listData() ? Object.keys(this.listData()).map(key => <SelectionListGroup>key) : [],
  );

  public tabsListData = computed(() =>
    this.availableTabs().reduce(
      (acc, tab: SelectionListGroup) => {
        const items = this.searchValue()
          ? this.filterItems(this.searchValue(), tab)
          : [...this.listData()[tab]!];
        const isSortable = this.fixedItemData[tab].sortable;

        acc[tab] = {
          ...this.fixedItemData[tab],
          itemGroups: this.isSortingEnabled() && isSortable ? this.sortItems(items) : [{ items }],
          sortable: this.isSortingEnabled() && isSortable,
        };

        return acc;
      },
      <GroupedSelectionItems<ItemData>>{},
    ),
  );

  public hasSearchEmptyState = computed<boolean>(
    () =>
      !!this.searchValue() &&
      this.tabsListData()[this.activeTab()]!.itemGroups.every(group => !group.items.length),
  );

  public activeTab = signal<SelectionListGroup>(SelectionListGroup.Job);

  private selectedJobs = signal<{ [k: number]: boolean }>({});
  private selectedTalentPools = signal<{ [k: number]: boolean }>({});
  private selectedJobTemplates = signal<{ [k: number]: boolean }>({});
  private activeSort = signal(SortOptions.Title);
  private countries: { [key: string]: string } = injectCountryList().reduce<Record<string, string>>(
    (countriesMap, country) => {
      countriesMap[country.code] = country.name;
      return countriesMap;
    },
    {},
  );

  public getSelectedListGroup(selectionGroup: SelectionListGroup): { [k: number]: boolean } {
    switch (selectionGroup) {
      case SelectionListGroup.Job:
        return this.selectedJobs();
      case SelectionListGroup.TalentPool:
        return this.selectedTalentPools();
      case SelectionListGroup.JobTemplate:
        return this.selectedJobTemplates();
      default:
        return {};
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.jobAssignments?.currentValue) {
      this.selectedJobs.set(
        this.jobAssignments().reduce((acc, id) => ({ ...acc, [id]: true }), {}),
      );
    }
    if (changes.talentPoolAssignments?.currentValue) {
      this.selectedTalentPools.set(
        this.talentPoolAssignments().reduce((acc, id) => ({ ...acc, [id]: true }), {}),
      );
    }
    if (changes.jobTemplateAssignments?.currentValue) {
      this.selectedJobTemplates.set(
        this.jobTemplateAssignments().reduce((acc, id) => ({ ...acc, [id]: true }), {}),
      );
    }
  }

  public toggleItem(item: { id: number }, selectionGroup: SelectionListGroup): void {
    switch (selectionGroup) {
      case SelectionListGroup.Job:
        this.selectedJobs.set({
          ...this.selectedJobs(),
          [item.id]: !this.selectedJobs()[item.id],
        });
        break;
      case SelectionListGroup.TalentPool:
        this.selectedTalentPools.set({
          ...this.selectedTalentPools(),
          [item.id]: !this.selectedTalentPools()[item.id],
        });
        break;
      case SelectionListGroup.JobTemplate:
        this.selectedJobTemplates.set({
          ...this.selectedJobTemplates(),
          [item.id]: !this.selectedJobTemplates()[item.id],
        });
        break;
      default:
        break;
    }

    this.emitSelected();
  }

  public resetSearch(): void {
    this.searchControl.reset();
  }

  public changeTab(tab: SelectionListGroup) {
    this.activeTab.set(tab);
  }

  public changeSortOption(sortOption: SortOptionsType): void {
    this.activeSort.set(sortOption);
  }

  public getJobStatusTranslation(offerStatus: string): string {
    return this.i18n.translate(`app.offers.status.${offerStatus}`);
  }

  public getSortLabelTranslation(sortOption: string): string {
    return this.i18n.translate(`app.offers.sort_by.${sortOption}`);
  }

  public get sortButtonLabel(): string {
    return this.getSortLabelTranslation(this.activeSort());
  }

  public get sortOptionsToArray(): SortOptions[] {
    return Object.values(this.SortOptions);
  }

  private filterItems(value: string, group: SelectionListGroup): SelectionList[] {
    return this.listData()[group]!.filter(item => {
      const title = item.title;
      return title ? includesWithTrim(title, value) : false;
    });
  }

  private sortItems(filteredItems: SelectionList[]): SortingGroup[] {
    switch (this.activeSort()) {
      case SortOptions.Title:
        return [{ items: this.sortAlphabetically(filteredItems) }];
      case SortOptions.Department:
      case SortOptions.Country:
      case SortOptions.Location:
        const groups = this.setSortingGroups(filteredItems, this.activeSort());
        return this.sortByItemsLength(groups);
      case SortOptions.Status:
        const statusGroups = this.setSortingGroups(filteredItems, this.activeSort());
        return statusGroups;
      default:
        return [];
    }
  }

  private setSortingGroups(filteredItems: SelectionList[], sort: SortOptionsType) {
    const isCountry = sort === SortOptions.Country;
    const groups = groupByKey(filteredItems, isCountry ? 'countryCode' : sort);

    switch (sort) {
      case SortOptions.Country:
        return Object.keys(groups).map(group => {
          const title = this.countries[group];
          return {
            groupTitle: title,
            items: groups[group],
          };
        });
      case SortOptions.Status:
        const sortedGroups = Object.keys(groups).map(group => {
          const title = this.getJobStatusTranslation(group);
          return {
            groupTitle: title,
            items: groups[group],
            key: group,
          };
        });
        return this.sortGroupsByStatus(sortedGroups);
      default:
        return Object.keys(groups).map(key => {
          const title =
            key === 'null' ? this.i18n.translate('app.assignments.editor.without_department') : key;
          return {
            groupTitle: title,
            items: groups[key],
          };
        });
    }
  }

  private sortGroupsByStatus(sortingGroups: SortingGroup[]): SortingGroup[] {
    const pattern = [
      OfferStatus.Published,
      OfferStatus.Internal,
      OfferStatus.Draft,
      OfferStatus.Closed,
    ];
    return sortingGroups.sort((a, b) => {
      const indexA = pattern.indexOf(<OfferStatus>a.key);
      const indexB = pattern.indexOf(<OfferStatus>b.key);
      return indexA - indexB;
    });
  }

  private sortAlphabetically(items: SelectionList[]): SelectionList[] {
    return [...items].sort((a, b) =>
      (a['title'] || '').toLowerCase().localeCompare((b['title'] || '').toLowerCase()),
    );
  }

  private sortByItemsLength(sortingGroups: SortingGroup[]): SortingGroup[] {
    return sortingGroups.sort((a, b) => b.items.length - a.items.length);
  }

  private emitSelected(): void {
    this.selectionChanges.emit({
      jobIds: this.getSelectedIds(this.selectedJobs()),
      talentPoolIds: this.getSelectedIds(this.selectedTalentPools()),
      jobTemplateIds: this.getSelectedIds(this.selectedJobTemplates()),
    });
  }

  private getSelectedIds(items: { [k: number]: boolean }): number[] {
    return Object.entries(items).reduce(
      (acc, [key, value]) => (value ? [...acc, parseInt(key, 10)] : acc),
      [],
    );
  }
}
